
import { CreateProjectRequest, FeatureManagementType, ProjectMetadata, ProjectTargetInput } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useProfiles } from "@api/profiles";
import { useCreateProject } from "@api/projects";
import { useAdditionalMetadataFeature } from "@hooks/useAdditionalMetadataFeature";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import _ from "lodash";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { BenchmarkTargetsForm } from "@features/project/components/ProjectPage/BenchmarkTargetsForm";
import { ProjectMetadataForm } from "./ProjectMetadataForm";

export interface DropdownOption {
  label: string;
  value: string;
}

interface AddProjectFormProps {
  existingProjects?: any[];
  handleCreateProject: (created: boolean) => void;
  handleCloseDialog: (closed: boolean) => void;
}

const AddProjectForm = (props: AddProjectFormProps) => {
  const appStore = useAppStore();
  const theme = useTheme();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createProject = useCreateProject();
  const appSettings = useAppSettings();
  const { isEnabled: showMetadata } = useAdditionalMetadataFeature();
  const isBenchmarkEnabled = appStore.hasFeature(FeatureManagementType.BenchmarkCatalog);
  const [benchmarkData, setBenchmarkData] = useState<{
    catalogId: string;
    targets: ProjectTargetInput[];
  }>({ catalogId: "", targets: [] });
  const [projectNumberError, setProjectNumberError] = useState<string>("");

  // TODO: type the form data
  const [formData, setFormData] = useState<CreateProjectRequest & { metadata?: ProjectMetadata }>(
    new CreateProjectRequest({
      name: "",
      description: "",
      projectEmissionReductionTarget: 0,
      rebarEstimation: (appSettings.data?.rebarEstimation && !appSettings.data?.profileLevelRebarSettings) ?? false,
      nonModelledElements: true,
      profileId: "",
    }),
  );

  // Initialize metadata state
  const [metadata, setMetadata] = useState<ProjectMetadata>(
    new ProjectMetadata({
      totalFloorArea: undefined,
      serviceLife: undefined,
      goal: "",
      scope: ""
    })
  );
  const [selectedProfileValue, setSelectedProfileValue] = useState<string>("");
  const [profileOptions, setProfileOptions] = useState<DropdownOption[]>([]);

  const profiles = useProfiles();

  useEffect(() => {
    if (profiles.data && profiles.data.length > 0) {
      const profileOptions = profiles.data.map((profile) => ({
        label: profile.name,
        value: profile.profileId,
      }));

      setProfileOptions(profileOptions as DropdownOption[]);
      const firstProfile = profileOptions[0];
      if (firstProfile) {
        setSelectedProfileValue(firstProfile.value || "");
        setFormData((prevData: any) => ({
          ...prevData,
          profileId: firstProfile.value || "",
        }));
      }
    }
  }, [profiles.data]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value } as any);
    console.log(event.target.value);
  };

  const handleRebarEstimationToggle = () => {
    setFormData({ ...formData, rebarEstimation: !formData.rebarEstimation } as any);
  };

  const handleNonModelledElementsToggle = () => {
    setFormData({ ...formData, nonModelledElements: !formData.nonModelledElements } as any);
  };


  const handleMetadataChange = (name: string, value: any) => {
    setMetadata(prev => {
      const newData = {
        totalFloorArea: prev.totalFloorArea,
        serviceLife: prev.serviceLife,
        goal: prev.goal,
        scope: prev.scope,
        [name]: value
      };
      return new ProjectMetadata(newData);
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitted(true);
    setIsSubmitting(true);
    console.log(formData);

    if (formData.name?.trim().length === 0 || formData.description?.trim().length === 0) {
      setIsSubmitting(false);
      return;
    }

    let created = false;
    try {
      const userId = appStore.userProfile?.userId!;

      if (!formData.profileId && profileOptions.length > 0) {
        formData.profileId = profileOptions[0].value;
      }

      // Validate Project Number when ADStages is enabled
      if (appStore.hasFeature(FeatureManagementType.ProjectExtendedDetails)) {
        const number = (formData as any).projectNumber ?? "";
        if (!/^\d{8}$/.test(number)) {
          setProjectNumberError(i18n.t("dashboard.helper.project-number-format"));
          setIsSubmitting(false);
          return;
        }
      }

      // Frontend duplicate check from provided existing projects (case-insensitive)
      if (appStore.hasFeature(FeatureManagementType.ProjectExtendedDetails)) {
        const number = (formData as any).projectNumber;
        if (number && props.existingProjects && props.existingProjects.length > 0) {
          const existsLocal = props.existingProjects?.some((p: any) => p?.projectNumber?.toLowerCase?.() === number.toLowerCase());
          if (existsLocal) {
            setProjectNumberError(i18n.t("dashboard.helper.project-number-duplicate"));
            setIsSubmitting(false);
            return;
          }
        }
      }
      const createProjectInput: any = createPayload(userId, formData);

      await createProject.mutateAsync(createProjectInput);
      created = true;
    } catch (error) {
      console.error('Error during project creation:', error);
    } finally {
      setIsSubmitting(false);
      if (created) {
        props.handleCreateProject(true);
        props.handleCloseDialog(true);
        setIsSubmitted(false);
      }
    }
  }


  const handleClosePopup = () => {
    props.handleCloseDialog(true);
  };

  function createPayload(userId: string, formData: any): CreateProjectRequest {
    console.log(formData);
    const payload = new CreateProjectRequest({
      userId,
      name: formData.name,
      description: formData.description,
      projectNumber: formData.projectNumber,
      projectEmissionReductionTarget: formData.projectEmissionReductionTarget,
      rebarEstimation: formData.rebarEstimation,
      nonModelledElements: formData.nonModelledElements,
      models: [],
      profileId: formData.profileId,
    });

    if (showMetadata) {
      const projectMetadata = new ProjectMetadata({
        totalFloorArea: metadata.totalFloorArea,
        serviceLife: metadata.serviceLife,
        goal: metadata.goal,
        scope: metadata.scope
      });
      (payload as CreateProjectRequest).projectMetadata = projectMetadata;
    }

    if (isBenchmarkEnabled && benchmarkData.catalogId) {
      payload.benchmarkCatalogEntityId = benchmarkData.catalogId;
      payload.projectTargets = benchmarkData.targets;
    }

    return payload;
  }

  const handleProfileChange = (event: SelectChangeEvent) => {
    const { value } = event.target;
    setSelectedProfileValue(value as string);

    const name = profileOptions.find((x) => x.value == value);

    setFormData({ ...formData, profileId: name?.value } as any);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack mt={1} spacing={2}>
        <TextField
          label={i18n.t("dashboard.title.project-name")}
          name="name"
          type="text"
          value={formData.name}
          onChange={handleInputChange}
          margin="normal"
          variant="outlined"
          fullWidth
          required
          error={isSubmitted && _.isEmpty(formData.name?.trim())}
          helperText={isSubmitted && _.isEmpty(formData.name?.trim()) ? i18n.t("dashboard.helper.project-name") : ""}
        />
        {appStore.hasFeature(FeatureManagementType.ProjectExtendedDetails) && (
          <TextField
            label={i18n.t("dashboard.title.project-number")}
            name="projectNumber"
            type="text"
            value={(formData as any).projectNumber || ""}
            placeholder="________"
            required
            onChange={(e) => {
              const raw = e.target.value || "";
              const digits = raw.replace(/\D/g, "");
              if (projectNumberError) setProjectNumberError("");
              setFormData({ ...formData, projectNumber: digits } as any);
              const number = digits;
              // length/format validation feedback
              if (number.length > 0 && number.length !== 8) {
                setProjectNumberError(i18n.t("dashboard.helper.project-number-format"));
                return;
              }
              // duplicate check when length is 8
              if (number && number.length === 8 && props.existingProjects && props.existingProjects.length > 0) {
                const existsLocal = props.existingProjects?.some((p: any) => p?.projectNumber?.toLowerCase?.() === number.toLowerCase());
                setProjectNumberError(existsLocal ? i18n.t("dashboard.helper.project-number-duplicate") : "");
              } else if (number.length === 0) {
                setProjectNumberError("");
              }
            }}
            inputProps={{ inputMode: "numeric", pattern: "\\d*", maxLength: 8 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={projectNumberError || i18n.t("dashboard.helper.project-number-format")}>
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            margin="normal"
            variant="outlined"
            fullWidth
            error={isSubmitted && !!projectNumberError}
            helperText={isSubmitted ? projectNumberError : ""}
          />
        )}
        <TextField
          label={i18n.t("dashboard.title.description")}
          name="description"
          type="text"
          value={formData.description}
          onChange={handleInputChange}
          margin="normal"
          variant="outlined"
          fullWidth
          required
          error={isSubmitted && _.isEmpty(formData.description?.trim())}
          helperText={isSubmitted && _.isEmpty(formData.description?.trim()) ? i18n.t("dashboard.helper.description") : ""}
        />
        <TextField
          label={i18n.t("dashboard.title.project-emission-reduction-target")}
          name="projectEmissionReductionTarget"
          type="number"
          value={formData.projectEmissionReductionTarget}
          onChange={handleInputChange}
          margin="normal"
          variant="outlined"
          fullWidth
          sx={{ display: "none" /* disabled for now per Carol */ }}
        />
        {(appSettings.data?.rebarEstimation || appSettings.data?.nonModelledElements) && (
          <Card elevation={3} sx={{ "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default } }}>
            <CardHeader title={<Typography variant="body2">{i18n.t("dashboard.title.project-settings")}</Typography>} elevation={3} />
            <CardContent sx={{ display: "flex", alignItems: "center", borderTop: 1, borderColor: theme.palette.divider }}>
              <Grid container spacing={2} sx={{ width: "100%" }}>
                <Grid size={12}>
                  <Grid container>
                    <Grid size="grow">
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body1"> {i18n.t("dashboard.title.profile-label")}</Typography>
                        <Typography variant="subtitle1" color={theme.palette.grey[500]} fontSize="0.75rem">
                          {i18n.t("dashboard.title.profile-description")}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size="auto">
                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel id="profile-label">{i18n.t("dashboard.title.select-profile")}</InputLabel>
                        <Select
                          labelId="profile-select"
                          id="profile-select"
                          value={selectedProfileValue}
                          onChange={handleProfileChange}
                          input={<OutlinedInput label={i18n.t("dashboard.title.select-profile")} />}
                        >
                          {profileOptions.map((profile) => (
                            <MenuItem key={profile.value} value={profile.value}>
                              {profile.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
            {!appSettings.data.profileLevelRebarSettings && appSettings.data?.rebarEstimation && (
              <CardContent sx={{ display: "flex", alignItems: "center", borderTop: 1, borderColor: theme.palette.divider }}>
                <Grid container spacing={2} sx={{ width: "100%" }}>
                  <Grid size={12}>
                    <Grid container>
                      <Grid size="grow">
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body1">{i18n.t("dashboard.title.rebar-estimation")}</Typography>
                          <Typography variant="subtitle1" color={theme.palette.grey[500]} fontSize="0.75rem">
                            {i18n.t("dashboard.description.rebar-estimation")}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size="auto">
                        <Switch checked={formData.rebarEstimation} onChange={handleRebarEstimationToggle} />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            )}
            {appSettings.data?.nonModelledElements && (
              <CardContent sx={{ display: "flex", alignItems: "center", borderTop: 1, borderColor: theme.palette.divider }}>
                <Grid container spacing={2} sx={{ width: "100%" }}>
                  <Grid size={12}>
                    <Grid container>
                      <Grid size="grow">
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body1">{i18n.t("dashboard.title.non-modelled-elements")}</Typography>
                          <Typography variant="subtitle1" color={theme.palette.grey[500]} fontSize="0.75rem">
                            {i18n.t("dashboard.description.non-modelled-elements")}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size="auto">
                        <Switch checked={formData.nonModelledElements} onChange={handleNonModelledElementsToggle} />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            )}
          </Card>
        )}
        {showMetadata && (
          <ProjectMetadataForm
            metadata={metadata}
            onChange={handleMetadataChange}
          />
        )}
        {isBenchmarkEnabled && (
          <BenchmarkTargetsForm
            onBenchmarkDataChange={(data) => setBenchmarkData(data)}
          />
        )}
        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <Grid container spacing={2} sx={{ justifyContent: "end" }}>
            <Grid>
              <Button variant="outlined" onClick={handleClosePopup}>
                {i18n.t("dashboard.title.close")}
              </Button>
            </Grid>
            <Grid>
              <Button
                autoFocus
                loading={isSubmitting}
                variant="contained"
                type="submit"
              >
                {i18n.t("dashboard.title.create")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </form>
  );
};

export default AddProjectForm;
